import { devtools } from "zustand/middleware";

export const isBrowser = () => typeof window !== "undefined";

/** Wrap a store with zustand devtools only in development (matches pet-n-sur pattern). */
export const devtoolsInNonProd = <T>(store: T): T =>
  process.env.NODE_ENV === "production" ? store : (devtools as unknown as (s: T) => T)(store);

// Re-export from formUtils so existing imports of these from helper.ts keep working.
export type { FormCondition } from "./formUtils";
export { evaluateCondition, isFieldHidden } from "./formUtils";

export function arraysEquality(array1: any[], array2: any[]) {
  return array1.length === array2.length && array1.every((value, index) => value === array2[index]);
}

export function objectEquality(object1: any, object2: any) {
  return Object.keys(object1).every(key => object1[key] === object2[key]);
}

export function splitArrayIntoChunks(array: any[], split: number) {
  const chunks = array.reduce((resultArray, item, index) => {
    const chunkIndex = Math.floor(index / split);

    if (!resultArray[chunkIndex]) {
      resultArray[chunkIndex] = [];
    }

    resultArray[chunkIndex].push(item);

    return resultArray;
  }, []);

  return chunks;
}

/**
 * Formats a number to NZ$ currency format.
 * Rounds up to the nearest 0.01 (second decimal place).
 * @param number - The number to format (can be null or undefined)
 * @returns Formatted currency string (e.g., "NZ$1,234.56") or the original value if not a number
 */
export const formatCurrency = (number: number | null | undefined): string => {
  if (typeof number !== "number" || isNaN(number)) {
    return "";
  }

  // Round up to the nearest 0.01 (second decimal place)
  const roundedNumber = Math.ceil(number * 100) / 100;

  return Intl.NumberFormat("en-NZ", {
    style: "currency",
    currency: "NZD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(roundedNumber);
};

/**
 * Parses a currency string back to a number.
 * Removes currency symbols, commas, and whitespace.
 * @param currencyString - The currency string to parse (e.g., "NZ$1,234.56" or "1234.56")
 * @returns The parsed number or 0 if invalid
 */
export const parseCurrency = (currencyString: string | null | undefined): number => {
  if (!currencyString || typeof currencyString !== "string") {
    return 0;
  }

  // Remove currency symbols, commas, and whitespace
  const cleaned = currencyString
    .replace(/NZ\$/gi, "")
    .replace(/[,\s]/g, "")
    .trim();

  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Formats input value as currency while the user types.
 * Allows partial input (e.g., "123" becomes "123.00" when formatted).
 * @param value - The input value from the user
 * @returns Formatted currency string without the NZ$ prefix (for display in input with prefix)
 */
export const formatCurrencyInput = (value: string | null | undefined): string => {
  if (!value || typeof value !== "string") {
    return "";
  }

  // Remove any existing currency formatting
  const cleaned = value.replace(/NZ\$/gi, "").replace(/[,\s]/g, "").trim();

  // If empty, return empty string
  if (!cleaned) {
    return "";
  }

  // Parse the number
  const number = parseFloat(cleaned);
  if (isNaN(number)) {
    // If not a valid number, return the cleaned value (allows partial typing like "12.")
    return cleaned;
  }

  // Format with thousand separators but keep decimals
  const parts = cleaned.split(".");
  const integerPart = parts[0] || "0";
  const decimalPart = parts[1] || "";

  // Add thousand separators to integer part
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  // Combine with decimal part if it exists
  return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
};